import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, AlertCircle, ChevronLeft } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  PLACE_TYPE_LABELS, AMENITY_LABELS, computeAllIn,
} from '../lib/listingConstants';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'sturent_admin_token';

const CHECKLIST_ITEMS = [
  { key: 'address_verified', label: 'Address verified' },
  { key: 'photos_look_real', label: 'Photos look real' },
  { key: 'pricing_reasonable', label: 'Pricing reasonable' },
  { key: 'description_clear', label: 'Description clear' },
  { key: 'no_discriminatory_language', label: 'No discriminatory language' },
];

const AdminListingReview = () => {
  const { listing_id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY) || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [checklist, setChecklist] = useState({});
  const [actionLoading, setActionLoading] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/admin/listings');
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/listings/${listing_id}`, {
          headers: { 'X-Admin-Token': token },
        });
        setData(res.data);
      } catch (e) {
        setErr('Could not load this listing.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing_id]);

  const allChecked = CHECKLIST_ITEMS.every((c) => !!checklist[c.key]);

  const callAction = async (path, body, successMsg) => {
    setActionLoading(path);
    try {
      await axios.post(`${API}/api/admin/listings/${listing_id}/${path}`,
        body || {},
        { headers: { 'X-Admin-Token': token } });
      toast.success(successMsg);
      navigate('/admin/listings');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed.');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-md mx-auto px-5 py-20 text-center">
          <AlertCircle className="h-10 w-10 mx-auto text-rose-500 mb-2" />
          <p className="text-sm text-slate-700">{err || 'Listing not found.'}</p>
          <Button className="mt-4" onClick={() => navigate('/admin/listings')}>Back</Button>
        </main>
      </div>
    );
  }

  const l = data.listing;
  const landlord = data.landlord;
  const total = l.total_monthly_cost_cad ?? computeAllIn(l);

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="admin-review-page">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        <Link to="/admin/listings" className="inline-flex items-center text-xs text-slate-600 hover:text-slate-900 mb-4" data-testid="admin-back">
          <ChevronLeft className="h-3 w-3 mr-1" /> Back to list
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Listing view */}
          <div className="lg:col-span-2 space-y-4">
            <div>
              <h1 className="text-2xl font-heading font-extrabold text-slate-900">{l.title}</h1>
              <p className="text-sm text-slate-600">{PLACE_TYPE_LABELS[l.place_type]} · {l.neighborhood ? `${l.neighborhood}, ` : ''}{l.city}</p>
              <span className="inline-block mt-2 text-[10px] font-bold uppercase px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full">{l.status?.replace(/_/g, ' ')}</span>
            </div>

            {/* Photos */}
            <div className="grid grid-cols-3 gap-2">
              {(l.photos || []).slice(0, 9).map((p) => {
                const src = p.signed_token ? `${API}/api/files/${p.signed_token}` : null;
                return (
                  <div key={p.file_id} className="aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden">
                    {src && <img src={src} alt="" className="w-full h-full object-cover" />}
                  </div>
                );
              })}
            </div>

            {/* Pricing breakdown */}
            <Card className="bg-amber-50 border-amber-200/60">
              <CardContent className="p-4 space-y-1 text-sm">
                <p className="font-semibold text-amber-900 mb-1">All-in total: ${total.toLocaleString()}/mo</p>
                <p className="text-xs text-amber-800">Rent ${l.monthly_rent_cad} · Utilities {l.includes_utilities ? 'incl' : `+$${l.additional_utility_cost_cad ?? 0}`} · Internet {l.includes_internet ? 'incl' : `+$${l.internet_cost_if_separate_cad ?? 0}`} · Parking {l.includes_parking ? 'incl' : `+$${l.parking_cost_if_separate_cad ?? 0}`}</p>
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4 text-sm space-y-1">
                <p className="font-semibold text-slate-900">Private address (admin view)</p>
                <p className="text-slate-700">{l.address_line_1}{l.address_line_2 ? `, ${l.address_line_2}` : ''}</p>
                <p className="text-slate-700">{l.city}, {l.province} {l.postal_code}</p>
                <p className="text-xs text-slate-500">Exact: {l.exact_location?.lat}, {l.exact_location?.lng} · Approx: {l.approximate_location?.lat}, {l.approximate_location?.lng}</p>
              </CardContent>
            </Card>

            {/* Description */}
            <div>
              <h2 className="font-heading font-bold text-slate-900 mb-1 text-sm">Description</h2>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{l.description_long}</p>
            </div>

            {/* Top features */}
            <div>
              <h2 className="font-heading font-bold text-slate-900 mb-1 text-sm">Top features</h2>
              <ul className="list-disc ml-5 text-sm text-slate-700">
                {(l.top_features || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>

            {/* Amenities */}
            <div>
              <h2 className="font-heading font-bold text-slate-900 mb-1 text-sm">Amenities ({(l.amenities || []).length})</h2>
              <div className="flex flex-wrap gap-1">
                {(l.amenities || []).map((a) => (
                  <span key={a} className="px-2 py-0.5 text-[11px] bg-slate-100 text-slate-700 rounded-full">{AMENITY_LABELS[a] || a}</span>
                ))}
              </div>
            </div>

            {/* Rules */}
            <div className="grid grid-cols-3 gap-2">
              <Card className="bg-white border-slate-200"><CardContent className="p-3 text-xs">Pets: {l.pets_allowed}</CardContent></Card>
              <Card className="bg-white border-slate-200"><CardContent className="p-3 text-xs">Smoking: {l.smoking_allowed}</CardContent></Card>
              <Card className="bg-white border-slate-200"><CardContent className="p-3 text-xs">Guests: {l.guests_allowed}</CardContent></Card>
            </div>
          </div>

          {/* Sidebar — landlord + actions */}
          <aside className="space-y-4">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4 space-y-2 text-sm">
                <p className="font-semibold text-slate-900">Landlord</p>
                <p className="text-slate-700">{landlord?.name || '—'}</p>
                <p className="text-xs text-slate-500">{landlord?.email}</p>
                {landlord?.identity_verified ? (
                  <p className="text-xs text-emerald-700 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Identity verified</p>
                ) : (
                  <p className="text-xs text-amber-700">Identity NOT verified (Phase 2)</p>
                )}
              </CardContent>
            </Card>

            {l.review_notes && (
              <Card className="bg-amber-50 border-amber-200/60">
                <CardContent className="p-3 text-xs">
                  <p className="font-semibold text-amber-900 mb-1">Previous review notes</p>
                  <p className="text-amber-800">{l.review_notes}</p>
                </CardContent>
              </Card>
            )}

            {l.status === 'submitted_for_review' && (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4 space-y-3">
                  <p className="font-semibold text-slate-900 text-sm">Verification checklist</p>
                  <p className="text-xs text-slate-500">All required to enable Approve.</p>
                  <div className="space-y-2">
                    {CHECKLIST_ITEMS.map((it) => (
                      <label key={it.key} className="flex items-center gap-2 text-xs text-slate-700" data-testid={`checklist-${it.key}`}>
                        <input
                          type="checkbox"
                          checked={!!checklist[it.key]}
                          onChange={(e) => setChecklist((c) => ({ ...c, [it.key]: e.target.checked }))}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                        {it.label}
                      </label>
                    ))}
                  </div>
                  <div className="pt-2 space-y-2">
                    <Button
                      className="w-full btn-primary"
                      disabled={!allChecked || actionLoading === 'approve'}
                      onClick={() => callAction('approve', { checklist }, 'Listing approved — it is now live.')}
                      data-testid="approve-btn"
                    >
                      {actionLoading === 'approve' && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Approve
                    </Button>
                    <Button variant="outline" className="w-full" onClick={() => setRejectOpen(true)} data-testid="reject-btn">Reject</Button>
                    <Button variant="outline" className="w-full" onClick={() => setChangesOpen(true)} data-testid="request-changes-btn">Request changes</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {l.status === 'approved' && (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4 space-y-2">
                  <Button variant="outline" className="w-full" onClick={() => callAction('pause', {}, 'Listing paused.')} data-testid="pause-btn">Pause</Button>
                  <Button variant="outline" className="w-full" onClick={() => callAction('archive', {}, 'Listing archived.')} data-testid="archive-btn">Archive</Button>
                </CardContent>
              </Card>
            )}
          </aside>
        </div>
      </main>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject listing</DialogTitle>
            <DialogDescription>The landlord will receive these notes by email.</DialogDescription>
          </DialogHeader>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            rows={5}
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
            placeholder="e.g. Photos look stock — we need original shots of the actual unit."
            data-testid="reject-notes"
          />
          <Button
            className="w-full btn-primary"
            disabled={notes.trim().length < 3}
            onClick={() => callAction('reject', { review_notes: notes }, 'Listing rejected.')}
            data-testid="reject-confirm"
          >
            Send rejection
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={changesOpen} onOpenChange={setChangesOpen}>
        <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md" data-testid="changes-dialog">
          <DialogHeader>
            <DialogTitle>Request changes</DialogTitle>
            <DialogDescription>Status returns to draft. Landlord can edit and resubmit.</DialogDescription>
          </DialogHeader>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            rows={5}
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
            placeholder="e.g. Add one exterior photo and clarify who else lives there."
            data-testid="changes-notes"
          />
          <Button
            className="w-full btn-primary"
            disabled={notes.trim().length < 3}
            onClick={() => callAction('request-changes', { review_notes: notes }, 'Changes requested.')}
            data-testid="changes-confirm"
          >
            Send to landlord
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminListingReview;
